import type { ReactElement, ReactNode } from 'react';
import type { GroupedAlert } from '@kirin/api-client/contracts/alerts';
import { cn } from '@/lib/utils';
import {
  FilePathCellValue,
  OutcomeBadge,
} from '@/components/DataTable/alerts/CellRenderers';
import { CopyToClipboardButton } from '@/components/shared/CopyToClipboardButton';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { MULTI_TOOL_TOOLTIP } from '@/components/shared/platform/platform-utils';
import { MULTI_TOOL_PLATFORM_ID } from '@kirin/api-client/contracts/platform-registry';
import { RiskScoreBar } from './RiskScoreBar';
import { InfoTooltip } from './InfoTooltip';
import {
  platformLabel,
  representativeEndpointLabel,
  representativeSecurityGroupLabel,
  representativeUserLabel,
} from './alert-detail-cards.helpers';

/**
 * KIR-4836 — optional per-cell explanations. Supplied only for the alert types
 * that define copy for them (today: `tool-call-scan`), so every other type's
 * strip renders exactly as before.
 */
export interface AlertFactsStripTooltips {
  outcome?: string;
  riskScore?: string;
}

/** KIR-4281 — `'occurrence'` renders the strip for one occurrence. */
export type AlertFactsStripVariant = 'group' | 'occurrence';

interface AlertFactsStripProps {
  alert: GroupedAlert;
  className?: string;
  /**
   * Badges shown on the Risk score line after the bar (severity, secret
   * confidence). Kept on one line, compacted, so the strip's height holds.
   */
  riskBadges?: ReactNode;
  /**
   * Shown without its alerts-table row (the alert window): adds the row's File,
   * and First seen under Last seen in one cell, which the table otherwise shows.
   */
  standalone?: boolean;
  /**
   * KIR-4281 — label to show instead of the one derived from `alert.endpoints`.
   * An occurrence has only an endpoint id and name, not the full record, so the
   * caller passes the label rather than the adapter inventing missing fields.
   */
  endpointLabel?: string | null;
  /**
   * KIR-4836 — the tool a `tool-call-scan` alert fired on, e.g. `Bash`. Shown
   * as its own cell so the Location card, which held nothing but this value,
   * can be dropped for that type.
   */
  toolName?: string;
  tooltips?: AlertFactsStripTooltips;
}

const FACT_LABEL =
  'whitespace-nowrap text-[10px] font-semibold uppercase tracking-wide text-muted-foreground';

function FactCell({
  label,
  tooltip,
  children,
}: {
  label: string;
  tooltip?: string;
  children: ReactNode;
}): ReactElement {
  return (
    <div className="flex min-w-0 flex-col justify-center px-4 py-2.5">
      <div className={cn('flex items-center gap-1', FACT_LABEL)}>
        {label}
        {tooltip !== undefined && (
          <InfoTooltip
            content={tooltip}
            label={label}
            className="[&>svg]:h-3 [&>svg]:w-3"
          />
        )}
      </div>
      <div className="mt-1 flex min-h-6 items-center text-sm font-medium">
        {children}
      </div>
    </div>
  );
}

/**
 * KIR-4481 — the scan-at-a-glance strip at the top of the redesigned Alert
 * Details panel: up to seven fields pulled from data already on the alert. The
 * headline cells (outcome, risk score, last seen) render regardless; the
 * attribution cells (security group, user, endpoint, IDE/platform) drop out when
 * the alert carries no such value. First Seen stays on the occurrences modal's
 * summary table rather than here, to keep the strip to one row (KIR-4481 review);
 * only the alert window, which has no table row, shows it, under Last seen.
 * The instance count lives under the actions bar's "Show Instances" instead.
 *
 * KIR-4836 adds an optional Tool cell and optional per-cell tooltips. Both are
 * opt-in props rather than type checks, so the strip stays type-agnostic and no
 * other alert type's rendering moves. The existing cell order is untouched for
 * the same reason.
 */
export function AlertFactsStrip({
  alert,
  className,
  toolName,
  tooltips,
  riskBadges,
  standalone = false,
  endpointLabel,
}: AlertFactsStripProps): ReactElement {
  const securityGroup = representativeSecurityGroupLabel(alert.securityGroups);
  const user = representativeUserLabel(alert.users);
  // An explicit label wins, `null` included, so occurrence mode never falls
  // back to the group's representative endpoint.
  const endpoint =
    endpointLabel !== undefined
      ? endpointLabel
      : representativeEndpointLabel(alert.endpoints);
  // Copy the whole endpoint name, never the "+N" overflow or the truncation.
  const endpointToCopy =
    endpointLabel !== undefined
      ? endpointLabel
      : alert.endpoints?.[0]
        ? alert.endpoints[0].name || alert.endpoints[0].machineId
        : null;
  const platform = platformLabel(alert.clientTypes);

  return (
    <div
      data-testid="alert-facts-strip"
      className={cn(
        'flex flex-wrap items-stretch rounded-md border',
        standalone
          ? // Nine cells wrap at window width: each row's cells grow to fill
            // it, and 1px gaps over the border colour draw the cell and row
            // separators, so the wrap reads as a tidy grid, not a ragged tail.
            'gap-px overflow-hidden bg-border [&>*]:grow [&>*]:bg-white dark:[&>*]:bg-slate-800'
          : 'divide-x divide-border bg-muted/20',
        className
      )}
    >
      {alert.outcome && (
        <FactCell
          label="Outcome"
          {...(tooltips?.outcome ? { tooltip: tooltips.outcome } : {})}
        >
          <OutcomeBadge outcome={alert.outcome} />
        </FactCell>
      )}
      <FactCell
        label="Risk score"
        {...(tooltips?.riskScore ? { tooltip: tooltips.riskScore } : {})}
      >
        {/* Compacted badges (22px) stay inside the cell's 24px value line. */}
        <div className="flex items-center gap-2 whitespace-nowrap [&_[data-slot=badge]]:py-0.5">
          <RiskScoreBar score={alert.score ?? null} />
          {riskBadges}
        </div>
      </FactCell>
      {toolName !== undefined && (
        <FactCell label="Tool">
          <span className="block max-w-[150px] truncate" title={toolName}>
            {toolName}
          </span>
        </FactCell>
      )}
      {securityGroup !== null && (
        <FactCell label="Security group">
          <span className="block max-w-[150px] truncate" title={securityGroup}>
            {securityGroup}
          </span>
        </FactCell>
      )}
      {user !== null && (
        <FactCell label="User">
          <span className="block max-w-[150px] truncate" title={user}>
            {user}
          </span>
        </FactCell>
      )}
      {endpoint !== null && (
        <FactCell label="Endpoint">
          <span
            className="block max-w-[150px] truncate font-mono text-xs"
            title={endpoint}
          >
            {endpoint}
          </span>
          {endpointToCopy && (
            <CopyToClipboardButton
              text={endpointToCopy}
              variant="ghost"
              compactCopiedState
              className="ml-0.5 h-6 w-6 shrink-0 p-0 text-muted-foreground hover:text-foreground"
            >
              <span className="sr-only">Copy endpoint</span>
            </CopyToClipboardButton>
          )}
        </FactCell>
      )}
      {platform !== null && (
        <FactCell label="Agent Platform">
          <span
            className="block max-w-[150px] truncate"
            title={
              alert.clientTypes?.length === 1 &&
              alert.clientTypes[0] === MULTI_TOOL_PLATFORM_ID
                ? MULTI_TOOL_TOOLTIP
                : platform
            }
          >
            {platform}
          </span>
        </FactCell>
      )}
      {alert.agentSessionId && (
        // KIR-3612: the session an Autonomous Execution Mode alert came from.
        <FactCell label="Session">
          <span
            className="block max-w-[300px] truncate font-mono text-xs"
            title={alert.agentSessionId}
          >
            {alert.agentSessionId}
          </span>
          <CopyToClipboardButton
            text={alert.agentSessionId}
            variant="ghost"
            compactCopiedState
            className="ml-0.5 h-6 w-6 shrink-0 p-0 text-muted-foreground hover:text-foreground"
          >
            <span className="sr-only">Copy session ID</span>
          </CopyToClipboardButton>
        </FactCell>
      )}
      {standalone && (
        <FactCell label="File">
          {/* The File column's own cell: middle truncation + copy. */}
          <span className="block w-[180px]">
            <FilePathCellValue
              filePath={alert.filePath}
              location={alert.location}
              eventKind={alert.eventKind}
            />
          </span>
        </FactCell>
      )}
      {standalone ? (
        // One cell, Last seen over First seen, with the times aligned.
        <div className="grid grid-cols-[auto_1fr] content-center items-center gap-x-2 gap-y-0.5 px-4 py-2.5 text-sm font-medium">
          <span className={FACT_LABEL}>Last Seen:</span>
          <RelativeTimeWithTooltip date={alert.lastSeenAt} />
          <span className={FACT_LABEL}>First Seen:</span>
          <RelativeTimeWithTooltip date={alert.firstSeenAt} />
        </div>
      ) : (
        <FactCell label="Last seen">
          <RelativeTimeWithTooltip date={alert.lastSeenAt} />
        </FactCell>
      )}
    </div>
  );
}

AlertFactsStrip.displayName = 'AlertFactsStrip';
